<!-- CollectorAssignmentModal.vue -->
<template>
  <div class="modal fade" id="collectorAssignmentModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-info text-white">
          <h5 class="modal-title">
            <i class="mdi mdi-account-plus me-2"></i>
            Assign Collector to Bill
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Alert Messages -->
          <div v-if="updateMessage" class="alert alert-dismissible fade show"
               :class="updateMessageType === 'success' ? 'alert-success' : 'alert-danger'" role="alert">
            <i :class="updateMessageType === 'success' ? 'mdi mdi-check-circle me-2' : 'mdi mdi-alert-circle me-2'"></i>
            {{ updateMessage }}
            <button type="button" class="btn-close" @click="clearUpdateMessage"></button>
          </div>

          <!-- Bill Information -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="alert alert-info border-info">
                <h6 class="alert-heading mb-2">
                  <i class="mdi mdi-file-document-outline me-2"></i>
                  Bill Information
                </h6>
                <div class="row">
                  <div class="col-md-6">
                    <strong>Bill Number:</strong><br>
                    <span class="text-primary fs-5">{{ selectedBillNo }}</span>
                  </div>
                  <div class="col-md-6">
                    <strong>Revenue Stream:</strong><br>
                    <span class="text-muted">{{ selectedBillIncomeTypeDescription }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Duplicate Bill Number Option -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="generateNewBillNumber" id="duplicateBillCheck">
                <label class="form-check-label fw-semibold text-warning" for="duplicateBillCheck">
                  <i class="mdi mdi-alert-circle me-1"></i>
                  Generate New Bill Number (Check if bill number is duplicate)
                </label>
                <small class="form-text text-muted d-block mt-1">
                  If checked, the system will automatically generate a new unique bill number for this invoice.
                </small>
              </div>
            </div>
          </div>

          <!-- Collector Search -->
          <div class="row mb-3">
            <div class="col-12">
              <label class="form-label">
                <i class="mdi mdi-account-search me-1"></i>
                Search Collector
              </label>
              <div class="input-group">
                <input v-model="collectorSearch" type="text" class="form-control"
                       placeholder="Search by name or ID number" @input="searchCollectors">
                <button class="btn btn-info" type="button" @click="searchCollectors">
                  <i class="bx bx-search"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Collectors Table -->
          <div class="row">
            <div class="col-12">
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead class="table-light">
                  <tr>
                    <th>ID No.</th>
                    <th>Name</th>
                    <th>Zone</th>
                    <th>Ward</th>
                    <th>Sub County</th>
                    <th>Action</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(user, index) in filteredCollectors" :key="index"
                      :class="{'table-success': selectedCollector && selectedCollector.idNo === user.idNo}">
                    <td>
                      <span class="fw-semibold">{{ user.idNo }}</span>
                    </td>
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="avatar-xs me-2">
                          <div class="avatar-title rounded-circle bg-info text-white">
                            {{ user.names.charAt(0).toUpperCase() }}
                          </div>
                        </div>
                        <span>{{ user.names }}</span>
                      </div>
                    </td>
                    <td>{{ user.zone || 'Not assigned' }}</td>
                    <td>{{ user.wardName }}</td>
                    <td>{{ user.subCountyName }}</td>
                    <td>
                      <button class="btn btn-sm"
                              :class="selectedCollector && selectedCollector.idNo === user.idNo ? 'btn-success' : 'btn-outline-info'"
                              @click="selectCollector(user)">
                        <i :class="selectedCollector && selectedCollector.idNo === user.idNo ? 'mdi mdi-check' : 'mdi mdi-account-plus'"></i>
                        {{ selectedCollector && selectedCollector.idNo === user.idNo ? 'Selected' : 'Select' }}
                      </button>
                    </td>
                  </tr>

                  <!-- No Results State -->
                  <tr v-if="filteredCollectors.length === 0 && !searchLoading && collectorSearch.trim()">
                    <td colspan="6" class="text-center text-muted py-4">
                      <i class="mdi mdi-account-search me-2"></i>
                      No collectors found matching "{{ collectorSearch }}"
                    </td>
                  </tr>

                  <!-- Initial State -->
                  <tr v-if="filteredCollectors.length === 0 && !searchLoading && !collectorSearch.trim()">
                    <td colspan="6" class="text-center text-muted py-4">
                      <i class="mdi mdi-information me-2"></i>
                      Enter a name or ID number to search for collectors
                    </td>
                  </tr>

                  <!-- Loading State -->
                  <tr v-if="searchLoading">
                    <td colspan="6" class="text-center py-4">
                      <div class="d-flex align-items-center justify-content-center">
                        <div class="spinner-border spinner-border-sm text-info me-2" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                        <span class="text-muted">Searching collectors...</span>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Selected Collector Summary -->
          <div v-if="selectedCollector" class="row mt-3">
            <div class="col-12">
              <div class="alert alert-success border-success">
                <h6 class="alert-heading mb-2">
                  <i class="mdi mdi-account-check me-2"></i>
                  Selected Collector
                </h6>
                <div class="row">
                  <div class="col-md-6">
                    <div class="d-flex align-items-center mb-2">
                      <div class="avatar-sm me-3">
                        <div class="avatar-title rounded-circle bg-success text-white">
                          {{ selectedCollector.names.charAt(0).toUpperCase() }}
                        </div>
                      </div>
                      <div>
                        <strong>{{ selectedCollector.names }}</strong><br>
                        <small class="text-muted">ID: {{ selectedCollector.idNo }}</small>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <strong>Assignment Details:</strong><br>
                    <small>Zone: {{ selectedCollector.zone || 'Not assigned' }}</small><br>
                    <small>Ward: {{ selectedCollector.wardName }}</small><br>
                    <small>Sub County: {{ selectedCollector.subCountyName }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- New Bill Number Success Display -->
          <div v-if="newBillNumberGenerated" class="row mt-3">
            <div class="col-12">
              <div class="alert alert-success border-success">
                <h6 class="alert-heading mb-3">
                  <i class="mdi mdi-check-circle text-success me-2"></i>
                  ✅ New Bill Number Generated Successfully!
                </h6>
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-2">
                      <strong class="text-muted">Previous Bill Number:</strong><br>
                      <span class="badge bg-secondary fs-6">{{ originalBillNumber }}</span>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-2">
                      <strong class="text-success">New Bill Number:</strong><br>
                      <div class="d-flex align-items-center">
                        <span class="badge bg-success fs-6 me-2">{{ newBillNumber }}</span>
                        <button type="button" class="btn btn-sm btn-outline-success" @click="copyBillNumber" :disabled="copyButtonLoading">
                          <i :class="copyButtonLoading ? 'mdi mdi-loading mdi-spin' : 'mdi mdi-content-copy'" class="me-1"></i>
                          {{ copyButtonText }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row mt-2">
                  <div class="col-12">
                    <small class="text-muted">
                      <i class="mdi mdi-information me-1"></i>
                      The new bill number has been assigned to avoid duplicates. The collector has been successfully assigned.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer bg-light">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="mdi mdi-close me-1"></i>
            {{ newBillNumberGenerated ? 'Close' : 'Cancel' }}
          </button>

          <button v-if="!newBillNumberGenerated"
                  type="button"
                  class="btn btn-info"
                  @click="assignCollector"
                  :disabled="!selectedCollector || updateLoading">
            <span v-if="updateLoading">
              <span class="spinner-border spinner-border-sm me-2" role="status"></span>
              Assigning...
            </span>
            <span v-else>
              <i class="mdi mdi-account-plus me-1"></i>
              Assign Collector
            </span>
          </button>

          <button v-if="newBillNumberGenerated"
                  type="button"
                  class="btn btn-success"
                  @click="copyBillNumber"
                  :disabled="copyButtonLoading">
            <i :class="copyButtonLoading ? 'mdi mdi-loading mdi-spin' : 'mdi mdi-content-copy'" class="me-1"></i>
            {{ copyButtonText }} Bill Number
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, biller, execute } from "@/api";

export default {
  name: "CollectorAssignmentModal",
  props: {
    selectedBillNo: {
      type: String,
      default: ''
    },
    selectedBillIncomeTypeDescription: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      collectorSearch: '',
      filteredCollectors: [],
      searchLoading: false,
      updateLoading: false,
      selectedCollector: null,

      // Duplicate bill handling
      generateNewBillNumber: false,

      // Messages
      updateMessage: '',
      updateMessageType: 'success',

      // New bill number display
      newBillNumberGenerated: false,
      newBillNumber: '',
      originalBillNumber: '',
      copyButtonText: 'Copy',
      copyButtonLoading: false
    }
  },
  watch: {
    selectedBillNo() {
      this.resetModal();
    }
  },
  methods: {
    resetModal() {
      this.collectorSearch = '';
      this.filteredCollectors = [];
      this.selectedCollector = null;
      this.generateNewBillNumber = false;
      this.newBillNumberGenerated = false;
      this.newBillNumber = '';
      this.originalBillNumber = '';
      this.copyButtonText = 'Copy';
      this.copyButtonLoading = false;
      this.clearUpdateMessage();
    },

    clearUpdateMessage() {
      this.updateMessage = '';
      this.updateMessageType = 'success';
    },

    showUpdateMessage(message, type = 'success') {
      this.updateMessage = message;
      this.updateMessageType = type;

      if (type === 'success') {
        setTimeout(() => {
          this.clearUpdateMessage();
        }, 5000);
      }
    },

    async searchCollectors() {
      if (!this.collectorSearch.trim()) {
        this.filteredCollectors = [];
        return;
      }

      this.searchLoading = true;

      try {
        const data = new FormData();
        data.append("function", "getUsersPaginated");
        data.append("page", 1);
        data.append("rows_per_page", 20);
        data.append("search", this.collectorSearch);

        const res = await execute(data, authUrl);
        this.searchLoading = false;

        if (res.data.success) {
          this.filteredCollectors = res.data.data.users;
        } else {
          this.showUpdateMessage(res.data.message, 'error');
        }
      } catch (e) {
        this.searchLoading = false;
        this.showUpdateMessage('Error searching collectors: ' + e.message, 'error');
      }
    },

    selectCollector(collector) {
      this.selectedCollector = collector;
      this.clearUpdateMessage();
    },

    async assignCollector() {
      if (!this.selectedCollector || !this.selectedBillNo) {
        this.showUpdateMessage('Please select a collector before assigning.', 'error');
        return;
      }

      this.updateLoading = true;
      this.clearUpdateMessage();

      try {
        const data = new FormData();
        data.append("function", "updateBillCollector");
        data.append("billNo", this.selectedBillNo);
        data.append("collectorIdNo", this.selectedCollector.idNo);
        data.append("generateNewBillNumber", this.generateNewBillNumber ? "true" : "false");
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));
        data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

        const res = await execute(data, biller);
        this.updateLoading = false;

        if (res.data.success) {
          let successMessage = `✅ Collector assigned successfully!\n\n👤 Collector: ${this.selectedCollector.names} (${this.selectedCollector.idNo})`;

          // Check if new bill number was generated
          if (res.data.data && res.data.data.billNumberGenerated && res.data.data.newBillNo) {
            this.newBillNumberGenerated = true;
            this.newBillNumber = res.data.data.newBillNo;
            this.originalBillNumber = res.data.data.originalBillNo || this.selectedBillNo;

            successMessage += `\n\n🆕 NEW BILL NUMBER: ${res.data.data.newBillNo}`;
            this.showUpdateMessage(successMessage, 'success');
          } else {
            this.showUpdateMessage(successMessage, 'success');

            // Close modal after delay for normal assignment
            setTimeout(() => {
              this.closeModal();
              this.$emit('collector-assigned', {
                billNo: this.selectedBillNo,
                collector: this.selectedCollector
              });
            }, 2000);
          }
        } else {
          this.showUpdateMessage('❌ Error: ' + res.data.message, 'error');
        }
      } catch (e) {
        this.updateLoading = false;
        this.showUpdateMessage('❌ Network error: ' + e.message, 'error');
      }
    },

    copyBillNumber() {
      this.copyButtonLoading = true;

      const tempInput = document.createElement('input');
      tempInput.value = this.newBillNumber;
      document.body.appendChild(tempInput);
      tempInput.select();

      try {
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(this.newBillNumber).then(() => {
            this.copySuccess();
          }).catch(() => {
            this.fallbackCopy(tempInput);
          });
        } else {
          this.fallbackCopy(tempInput);
        }
      } catch (err) {
        this.fallbackCopy(tempInput);
      }

      document.body.removeChild(tempInput);
    },

    fallbackCopy(tempInput) {
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          this.copySuccess();
        } else {
          this.copyError();
        }
      } catch (err) {
        this.copyError();
      }
    },

    copySuccess() {
      this.copyButtonText = 'Copied!';
      this.copyButtonLoading = false;
      this.showUpdateMessage(`✅ Bill number ${this.newBillNumber} copied to clipboard!`, 'success');

      setTimeout(() => {
        this.copyButtonText = 'Copy';
      }, 2000);
    },

    copyError() {
      this.copyButtonLoading = false;
      this.showUpdateMessage('❌ Failed to copy bill number', 'error');
    },

    closeModal() {
      const modalElement = document.getElementById('collectorAssignmentModal');
      if (modalElement) {
        const modal = bootstrap.Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        }
      }
    }
  }
}
</script>

<style scoped>
.modal-header.bg-info {
  border-bottom: none;
}

.modal-footer.bg-light {
  border-top: 1px solid #dee2e6;
}

.table th {
  font-weight: 600;
  color: #495057;
  background-color: #f8f9fa !important;
}

.badge {
  font-size: 0.75rem;
}

.badge.fs-6 {
  font-size: 0.875rem !important;
}

.avatar-xs {
  width: 2rem;
  height: 2rem;
}

.avatar-sm {
  width: 2.5rem;
  height: 2.5rem;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
}

.text-info {
  color: #0dcaf0 !important;
}

.bg-info {
  background-color: #0dcaf0 !important;
}

.border-info {
  border-color: #0dcaf0 !important;
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}

.alert-info {
  background-color: rgba(13, 202, 240, 0.1);
  border-color: rgba(13, 202, 240, 0.2);
  color: #055160;
}

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}
</style>